import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CoatingsProps } from '@partsbadger/types';
import { Button, Input, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { StepCard } from '../StepCard';
import _ from 'lodash';
import { FilterPill } from '../FilterPill';

interface IProduct {
    coatings?: number[];
    custom_coating?: string;
}
interface ICoatingStepProps {
    stepIndex?: number;
    coatings: CoatingsProps[];
    topCoatings: CoatingsProps[];
    coatingSelected: CoatingsProps | null;
    onNext?: () => void;
    onPrev?: () => void;
    isHobbyist: boolean;
    loading: boolean;
    customCoating: string | null;
    onHandleUpdate: (value: { product: IProduct }) => void;
    onPendingCustomChange?: (stepIndex: number, isPending: boolean) => void;
}

const { Search } = Input;

export function CoatingStep({
    stepIndex = 3,
    coatings,
    topCoatings,
    coatingSelected,
    onNext,
    onPrev,
    isHobbyist,
    loading,
    customCoating,
    onHandleUpdate,
    onPendingCustomChange,
}: ICoatingStepProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All Coatings');
    const [pendingCustomCoatingId, setPendingCustomCoatingId] = useState<number | null>(null);
    const shouldAutoAdvance = useRef(false);

    let data = coatings;

    if (isHobbyist) {
        data = data?.filter(item => {
            return item.name !== 'Review 2D' && item.name !== 'Custom';
        });
    }

    const custom = data.find(item => item.name === 'Custom');

    if (custom) {
        // Exclude custom from list
        data = data.filter(item => item.id !== custom?.id);
    }

    const displayCoatingSelected = pendingCustomCoatingId != null && custom ? custom : coatingSelected;

    useEffect(() => {
        onPendingCustomChange?.(stepIndex, pendingCustomCoatingId != null);
    }, [stepIndex, pendingCustomCoatingId, onPendingCustomChange]);

    const handleUpdate = (product: IProduct) => {
        const payload = {
            product: product,
        };

        onHandleUpdate(payload);
    };

    const handleOptionSelect = (id: number) => {
        if (custom && id === custom.id) {
            onPendingCustomChange?.(stepIndex, true);
            setPendingCustomCoatingId(custom.id);
            handleUpdate({ coatings: [custom.id], custom_coating: '' });
            return;
        }
        onPendingCustomChange?.(stepIndex, false);
        setPendingCustomCoatingId(null);
        shouldAutoAdvance.current = true;
        const payload: IProduct = { coatings: [id], custom_coating: '' };
        handleUpdate(payload);
    };

    const clearSearch = () => {
        setSearchQuery('');
    };

    const categoryCounts = data.reduce<Record<string, number>>((acc, item) => {
        const categories = item?.categories || [];

        if (Array.isArray(categories) && categories.length > 0) {
            categories.forEach((category: string) => {
                const categoryName = category || 'Unspecified';
                acc[categoryName] = (acc[categoryName] || 0) + 1;
            });
        } else {
            // Handle items with no categories
            acc['Unspecified'] = (acc['Unspecified'] || 0) + 1;
        }

        return acc;
    }, {});

    const categoryList: Array<{ name: string; count: number }> = [
        { name: 'All Coatings', count: data.length },
        ...Object.entries(categoryCounts).map(([name, count]) => ({
            name: name || 'Unspecified', // Handle empty string
            count,
        })),
    ];

    // Filter materials based on search and category
    const filteredData = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        let filtered = [...data];

        // Filter by category
        if (selectedCategory !== 'All Coatings') {
            filtered = filtered.filter(item => {
                const categories = item?.categories || [];
                return Array.isArray(categories) && categories.includes(selectedCategory);
            });
        }

        if (custom) {
            filtered = [custom, ...filtered];
        }

        // Filter by search query
        if (query) {
            filtered = filtered.filter(item => item?.name.toLowerCase().includes(query));

            if (filtered.length == 0) {
                return filtered;
            }
        }
        const shouldShowSelected =
            selectedCategory === 'All Coatings' || coatingSelected?.categories?.includes(selectedCategory);

        if (displayCoatingSelected && shouldShowSelected) {
            filtered = filtered.filter(m => m?.id !== displayCoatingSelected.id);
            filtered = [displayCoatingSelected, ...filtered];
        }

        const sorted = filtered.slice().sort((a, b) => {
            const aIsNone = a.name === 'None';
            const bIsNone = b.name === 'None';
            if (aIsNone && !bIsNone) return -1;
            if (!aIsNone && bIsNone) return 1;
            return 0;
        });

        return sorted;
    }, [searchQuery, selectedCategory, displayCoatingSelected, custom, data]);

    // const filteredTopCoatings = useMemo(() => {
    //     const coatingIds = new Set(data?.map(d => d.id));

    //     let filtered = topCoatings.filter(item => coatingIds.has(item.id));

    //     // Filter by category
    //     if (selectedCategory !== 'All Coatings') {
    //         filtered = filtered.filter(item => {
    //             const categories = item?.categories || [];
    //             return Array.isArray(categories) && categories.includes(selectedCategory);
    //         });
    //     }

    //     // Filter by search query
    //     if (searchQuery.trim()) {
    //         const query = searchQuery.toLowerCase();
    //         filtered = filtered.filter(item => item?.name.toLowerCase().includes(query));
    //     }

    //     // Return an array with selected material first (if exists), then the rest
    //     return filtered;
    // }, [searchQuery, selectedCategory, data, topCoatings]);

    // Get popular materials within the filtered results
    // const popularMaterials = filteredMaterials.filter(m => m.popular);
    const otherCoatings = filteredData;

    const handleCategorySelect = (categoryId: string) => {
        setSelectedCategory(categoryId);
        setSearchQuery(''); // Clear search when changing category
    };

    return (
        <div data-testid="coating-step-shell" className="space-y-4">
            <div>
                <h3 data-testid="coating-step-heading" className="text-lg font-semibold text-slate-900">
                    Coatings
                </h3>
                <p className="text-slate-600 text-sm">Add protective coatings to enhance durability and appearance</p>
            </div>

            {/* Search Bar */}
            <div className="relative">
                <Search
                    data-testid="coating-search-input"
                    placeholder="Search coatings..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    allowClear
                />
            </div>

            {/* Category Filter */}
            {/* <div className="space-y-2">
                <h4 className="text-xs font-medium text-slate-700">Filter by Coating Category</h4>
                <div className="flex flex-wrap gap-1">
                    {categoryList?.map(category => {
                        const isSelected = selectedCategory === category.name;
                        const categoryCount = category.count;

                        return (
                            <FilterPill
                                key={category.name}
                                label={category.name}
                                count={categoryCount}
                                isSelected={isSelected}
                                onSelect={handleCategorySelect}
                            />
                        );
                    })}
                </div>
            </div> */}

            {/* Results */}
            {filteredData.length === 0 ? (
                <div data-testid="coating-empty-state" className="text-center py-8">
                    <h4 className="text-sm font-medium text-slate-600 mb-1">
                        The selected material may be limiting the available coating options. Please review your material
                        selection.
                    </h4>
                    {custom && (
                        <span className="flex flex-row items-center cursor-pointer justify-center mb-2">
                            Can't find your Coating? Please select{' '}
                            <Button
                                type="link"
                                onClick={() => {
                                    setSearchQuery('Custom');
                                    if (custom?.id != null) {
                                        onPendingCustomChange?.(stepIndex, true);
                                        setPendingCustomCoatingId(custom.id);
                                        handleUpdate({ coatings: [custom.id], custom_coating: '' });
                                    }
                                }}
                                className="p-1"
                            >
                                Custom
                            </Button>
                        </span>
                    )}

                    <Button
                        type="default"
                        size="small"
                        onClick={() => {
                            setSearchQuery('');
                            setSelectedCategory('All Coatings');
                        }}
                    >
                        Clear all filters
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {/* {filteredTopCoatings?.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">Popular Choices</h4>
                            <motion.div layout className="grid gap-1">
                                {filteredTopCoatings?.map(item => (
                                    <StepCard
                                        key={item?.id}
                                        stepInfo={{ id: item.id, name: item.name }}
                                        selectedId={displayCoatingSelected?.id}
                                        onSelect={handleOptionSelect}
                                        customValue={customCoating}
                                        onUpdateCustom={value => {
                                            handleUpdate({ coatings: [custom!.id], custom_coating: value });
                                            setPendingCustomCoatingId(null);
                                        }}
                                        isPopular={true}
                                    />
                                ))}
                            </motion.div>
                        </div>
                    )} */}

                    {otherCoatings?.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                {selectedCategory === 'All Coatings'
                                    ? 'All Coatings'
                                    : `${categoryList?.find(c => c.name === selectedCategory)?.name || ''}`}
                            </h4>

                            <Spin spinning={loading} indicator={<LoadingOutlined />}>
                                <div
                                    className="grid gap-1 overflow-y-auto pr-2 pl-1"
                                    style={{ maxHeight: 'min(45vh, 380px)' }}
                                >
                                    {otherCoatings?.map(item => (
                                        <StepCard
                                            key={item?.id}
                                            entity="coating"
                                            stepInfo={{ id: item.id, name: item.name }}
                                            selectedId={displayCoatingSelected?.id}
                                            onSelect={handleOptionSelect}
                                            customValue={customCoating}
                                            onUpdateCustom={value => {
                                                onPendingCustomChange?.(stepIndex, false);
                                                handleUpdate({ coatings: [custom!.id], custom_coating: value });
                                                setPendingCustomCoatingId(null);
                                            }}
                                        />
                                    ))}
                                </div>
                            </Spin>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
